
import React, { useState } from 'react';
import { UserData, Language, Gender } from '../types';

interface InputFormProps {
  onSubmit: (data: UserData) => void;
  isLoading: boolean;
}

const InputForm: React.FC<InputFormProps> = ({ onSubmit, isLoading }) => {
  // Local state for split fields
  const [name, setName] = useState('');
  const [gender, setGender] = useState<Gender | ''>('');
  const [birthLocation, setBirthLocation] = useState('');
  const [language, setLanguage] = useState<Language | ''>('');

  // Date fields
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');

  // Time fields
  const [hour, setHour] = useState('12');
  const [minute, setMinute] = useState('00');
  const [ampm, setAmpm] = useState('AM');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name || !gender || !day || !month || !year || !birthLocation || !language) {
        alert("Please fill in all the required fields.");
        return;
    }

    // Construct Date: YYYY-MM-DD
    const padDay = day.padStart(2, '0');
    const padMonth = month.padStart(2, '0');
    const constructedDate = `${year}-${padMonth}-${padDay}`;

    // Construct Time: HH:MM (24-hour format)
    let h = parseInt(hour, 10);
    if (ampm === 'PM' && h < 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;
    const hStr = h.toString().padStart(2, '0');
    const constructedTime = `${hStr}:${minute}`;

    const userData: UserData = {
        name,
        gender,
        birthDate: constructedDate,
        birthTime: constructedTime,
        birthLocation,
        language
    };

    onSubmit(userData);
  };

  // Helper arrays
  const months = [
    { value: '1', label: 'January' }, { value: '2', label: 'February' },
    { value: '3', label: 'March' }, { value: '4', label: 'April' },
    { value: '5', label: 'May' }, { value: '6', label: 'June' },
    { value: '7', label: 'July' }, { value: '8', label: 'August' },
    { value: '9', label: 'September' }, { value: '10', label: 'October' },
    { value: '11', label: 'November' }, { value: '12', label: 'December' }
  ];

  const hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
  const minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

  // Styles
  const labelClasses = "block text-gold-500 text-xs font-bold uppercase tracking-wider mb-2 ml-1";
  // Updated input style to match the dark screenshot look
  const inputBaseClasses = "bg-[#232142] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold-500/50 transition-all [color-scheme:dark]";
  const inputClasses = `w-full px-4 py-3 ${inputBaseClasses}`;
  const selectClasses = `appearance-none cursor-pointer ${inputClasses}`;
  
  // Class to hide number input spinners
  const noSpinnerClasses = "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";

  return (
    <div className="relative z-10 w-full max-w-md mx-auto p-4 sm:p-6">
      <div className="text-center mb-6 sm:mb-8">
        <h1 className="text-3xl sm:text-4xl font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-500 to-yellow-200 mb-2 drop-shadow-md">
          Celestial Guide
        </h1>
        <p className="text-gray-300 font-sans text-xs sm:text-sm tracking-wide">
          Enter your birth details to reveal your cosmic path.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 sm:p-8 border border-white/10 shadow-[0_8px_32px_0_rgba(0,0,0,0.37)] space-y-5">
        
        {/* Name */}
        <div>
          <label className={labelClasses}>Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter your full name"
            className={inputClasses}
            autoComplete="name"
            required
          />
        </div>

        {/* Gender */}
        <div>
          <label className={labelClasses}>Gender</label>
          <div className="relative">
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value as Gender)}
              className={`${selectClasses} ${!gender ? 'text-gray-400' : 'text-white'}`}
              required
            >
              <option value="" disabled>Select Gender</option>
              <option value={Gender.Male}>Male</option>
              <option value={Gender.Female}>Female</option>
              <option value={Gender.Other}>Other</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gold-500">
              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
            </div>
          </div>
        </div>

        {/* Split Birth Date */}
        <div>
          <label className={labelClasses}>Birth Date</label>
          <div className="flex gap-3">
             <div className="w-1/4">
                 <input 
                    type="number" 
                    placeholder="Day" 
                    value={day}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (val.length <= 2) setDay(val);
                    }}
                    className={`${inputClasses} text-center ${noSpinnerClasses}`}
                    min="1" max="31"
                    required
                 />
             </div>
             <div className="relative w-2/4">
                <select
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    className={`${selectClasses} ${!month ? 'text-gray-500' : 'text-white'}`}
                    required
                >
                    <option value="" disabled>Month</option>
                    {months.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-gold-500">
                    <svg className="fill-current h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                </div>
             </div>
             <div className="w-1/4">
                 <input 
                    type="number" 
                    placeholder="Year" 
                    value={year}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (val.length <= 4) setYear(val);
                    }}
                    className={`${inputClasses} text-center ${noSpinnerClasses}`}
                    min="1900" max={new Date().getFullYear()}
                    required
                 />
             </div>
          </div>
        </div>

        {/* Split Birth Time */}
        <div>
          <label className={labelClasses}>
            Birth Time <span className="text-gray-400 text-xs font-bold ml-2 tracking-wider">(OPTIONAL)</span>
          </label>
          <div className="flex gap-3">
             {/* Hour */}
             <div className="relative w-1/3">
                <select
                    value={hour}
                    onChange={(e) => setHour(e.target.value)}
                    className={`${selectClasses} text-center`}
                >
                    {hours.map(h => (
                        <option key={h} value={h}>{h.padStart(2, '0')}</option>
                    ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none text-gold-500">
                    <svg className="fill-current h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                </div>
             </div>

             {/* Minute */}
             <div className="relative w-1/3">
                <select
                    value={minute}
                    onChange={(e) => setMinute(e.target.value)}
                    className={`${selectClasses} text-center`}
                >
                    {minutes.map(m => (
                        <option key={m} value={m}>{m}</option>
                    ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none text-gold-500">
                    <svg className="fill-current h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                </div>
             </div>

             {/* AM/PM */}
             <div className="relative w-1/3">
                <select
                    value={ampm}
                    onChange={(e) => setAmpm(e.target.value)}
                    className={`${selectClasses} text-center`}
                >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none text-gold-500">
                    <svg className="fill-current h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                </div>
             </div>
          </div>
        </div>

        {/* Location */}
        <div>
          <label className={labelClasses}>Birth Place</label>
          <input
              type="text"
              value={birthLocation}
              onChange={(e) => setBirthLocation(e.target.value)}
              placeholder="City, Country"
              className={inputClasses}
              required
          />
        </div>

        {/* Language */}
        <div>
          <label className={labelClasses}>Output Language</label>
          <div className="relative">
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value as Language)}
              className={`${selectClasses} ${!language ? 'text-gray-400' : 'text-white'}`}
              required
            >
              <option value="" disabled>Select Language</option>
              <option value={Language.English}>English</option>
              <option value={Language.Tamil}>தமிழ் (Tamil)</option>
              <option value={Language.Sinhala}>සිංහල (Sinhala)</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gold-500">
              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full mt-6 bg-gradient-to-r from-gold-600 via-gold-500 to-gold-600 text-black font-serif font-bold py-3.5 px-4 rounded-lg shadow-[0_0_20px_rgba(255,215,0,0.2)] hover:shadow-[0_0_30px_rgba(255,215,0,0.4)] transform hover:-translate-y-0.5 transition-all disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none bg-[length:200%_auto] hover:bg-right"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-3">
              <svg className="animate-spin h-5 w-5 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Divining...
            </span>
          ) : (
            "Reveal My Destiny"
          )}
        </button>
      </form>
    </div>
  );
};

export default InputForm;
